import { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { FileTypeIcon } from '@/shared/ui/FileTypeIcon.jsx';
import { Download, AlertCircle, Shield, Loader2, Lock, Unlock } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { TruncatedTooltip } from '@/shared/ui/TruncatedTooltip.jsx';
import LoadingSpinner from '@/shared/ui/LoadingSpinner';
import { FilePreview } from '@/shared/ui/FilePreview';
import { GuestFolderBrowser } from '@/pages/GuestFolderBrowser.jsx';
import { SigningPanel } from '@/modules/signing/components/SigningPanel';
import { publicRequest } from '@/shared/utils/request';
import {
  GUEST_SHARE_RESOURCE,
  GUEST_AUTH_CHALLENGE,
  GUEST_AUTH_VERIFY,
} from '@/shared/utils/urls';
import { useEIMZO } from '@/hooks/useEIMZO';
import { maskNationalId } from '@/shared/utils/nationalId';
import { apiErrorMessage } from '@/shared/utils/apiError';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';


export const GuestShareView = () => {
  const { t } = useTranslation(['signing', 'common']);
  const { token } = useParams();
  const [shareData, setShareData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Guest auth state
  const [guestToken, setGuestToken] = useState(null);
  const [authLoading, setAuthLoading] = useState(false);
  const eimzo = useEIMZO();

  // Clean up guest token on unmount
  useEffect(() => {
    return () => {
      localStorage.removeItem('guestToken');
    };
  }, []);

  // Fetch the shared resource
  useEffect(() => {
    const fetchResource = async () => {
      try {
        const response = await publicRequest.get(`/${GUEST_SHARE_RESOURCE}`, {
          params: { share_token: token },
        });
        setShareData(response.data);
      } catch (err) {
        console.error('Error fetching guest share resource:', err);
        setError(apiErrorMessage(err));
      } finally {
        setLoading(false);
      }
    };

    if (token) fetchResource();
  }, [token]);


  // Guest auth: connect EIMZo and load certificates
  const handleConnectEIMZO = async () => {
    try {
      await eimzo.connect();
      await eimzo.loadCertificates();
    } catch (err) {
      toast.error(t('signing:eimzo.connectionFailed'));
    }
  };

  // Guest auth: challenge + verify flow
  const handleGuestAuth = useCallback(async () => {
    if (!eimzo.selectedCertificate) {
      toast.error(t('signing:eimzo.selectCertificate'));
      return;
    }

    try {
      setAuthLoading(true);

      // Step 1: Get challenge
      const challengeRes = await publicRequest.post(`/${GUEST_AUTH_CHALLENGE}`);
      const { requestId, challenge } = challengeRes.data;

      // Step 2: Load key and sign challenge
      const keyId = await eimzo.loadKey(eimzo.selectedCertificate);
      const pkcs7 = await eimzo.signData(challenge, keyId);

      // Step 3: Verify with server
      const verifyRes = await publicRequest.post(`/${GUEST_AUTH_VERIFY}`, {
        requestId,
        pkcs7,
      });

      const { accessToken } = verifyRes.data;
      if (accessToken) {
        setGuestToken(accessToken);
        // Store so auth() picks it up for all subsequent signing API calls
        localStorage.setItem('guestToken', accessToken);
        toast.success(t('signing:guest.authSuccess'));
      }
    } catch (err) {
      console.error('Guest auth error:', err);
      toast.error(t('signing:guest.authFailed'));
    } finally {
      setAuthLoading(false);
    }
  }, [eimzo]);

  // Handle certificate selection
  const handleSelectCertificate = (certId) => {
    const cert = eimzo.certificates.find(
      (c) => (c.serialNumber || c.id) === certId
    );
    eimzo.setSelectedCertificate(cert);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <LoadingSpinner size={40} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Card className="w-full max-w-md">
          <CardContent className="flex flex-col items-center justify-center p-8 text-center">
            <AlertCircle className="w-16 h-16 text-red-500 mb-4" />
            <h2 className="text-xl font-semibold text-foreground mb-2">
              {t('signing:guest.accessError')}
            </h2>
            <p className="text-muted-foreground">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // The linked node: a file, or — where the project allows folder links — a
  // folder the guest browses. Download & print follow the link's setting.
  const file = shareData?.node;
  const allowDownload = shareData?.allowDownload !== false;
  const until = shareData?.expiresAt ? new Date(shareData.expiresAt).toLocaleDateString() : null;

  if (file?.type === 'folder') {
    return (
      <div className="min-h-screen bg-background py-8 px-4">
        <div className="mx-auto w-full max-w-5xl space-y-4">
          <div>
            <h1 className="text-lg font-semibold text-foreground">{shareData.label || file.name}</h1>
            {until && <p className="text-xs text-muted-foreground">{t('common:guestShare.until', { date: until })}</p>}
          </div>
          <GuestFolderBrowser token={token} root={file} allowDownload={allowDownload} />
        </div>
      </div>
    );
  }

  if (!file) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Card className="w-full max-w-md">
          <CardContent className="flex flex-col items-center justify-center p-8 text-center">
            <AlertCircle className="w-16 h-16 text-yellow-500 mb-4" />
            <h2 className="text-xl font-semibold text-foreground mb-2">
              {t('signing:guest.noFile')}
            </h2>
            <p className="text-muted-foreground">{t('signing:guest.noFileDesc')}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const ext = (file.extension || 'txt').toLowerCase();
  const filename = file.name;
  const docType = file.approvalState || file.approval_state;
  const showSigningPanel = docType === 'signed';

  return (
    <div className="min-h-screen bg-background py-8 px-4">
      <div className="mx-auto w-full max-w-[1600px]">
        {/* File header */}
        <Card className="mb-4">
          <CardHeader className="flex flex-row items-center gap-3 p-3">
            <FileTypeIcon file={file} className="w-10 h-10 shrink-0" />
            <div className="flex-1 min-w-0">
              <TruncatedTooltip content={filename} className="flex-1 min-w-0">
                <h1 className="text-base font-semibold text-foreground truncate">
                  {filename}
                </h1>
              </TruncatedTooltip>
              <p className="text-xs text-muted-foreground">
                {ext.toUpperCase()}
                {until && ` · ${t('common:guestShare.until', { date: until })}`}
              </p>
            </div>
            {allowDownload && file.previewUrl && (
              <Button
                asChild
                size="sm"
                className="text-white font-medium h-8"
                style={{
                  background: 'linear-gradient(135deg, #4185F6 0%, #6FA4F9 100%)',
                }}
              >
                <a href={file.previewUrl} download={filename}>
                  <Download className="w-3.5 h-3.5 mr-1.5" />
                  {t('common:editor.download')}
                </a>
              </Button>
            )}
          </CardHeader>
        </Card>

        {/* Content: preview + signing panel or guest auth */}
        <div className={`flex flex-col sm:flex-row gap-6 ${showSigningPanel ? 'sm:items-start' : ''}`}>
          <div className="flex-1 min-w-0">
            <FilePreview file={file} shareToken={token} />
          </div>

          {showSigningPanel && (
            <div className="w-full sm:w-80 shrink-0 space-y-4">
              {/* Guest Auth Panel - show when not authenticated */}
              {!guestToken ? (
                <Card className="h-fit bg-card border-border">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base flex items-center gap-2 text-foreground">
                      <Shield className="w-5 h-5 text-purple-600" />
                      {t('signing:guest.authTitle')}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <p className="text-sm text-muted-foreground">
                      {t('signing:guest.authDescription')}
                    </p>

                    <div className="h-px bg-border my-2" />

                    {!eimzo.connected ? (
                      <Button
                        variant="outline"
                        className="w-full"
                        onClick={handleConnectEIMZO}
                        disabled={eimzo.connecting}
                      >
                        {eimzo.connecting ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <Lock className="w-4 h-4 mr-2" />
                        )}
                        {t('signing:eimzo.connect')}
                      </Button>
                    ) : (
                      <div className="space-y-3">
                        <div className="flex items-center gap-2 text-sm text-green-500 font-medium bg-green-500/10 p-2 rounded border border-green-500/20 dark:bg-green-900/20 dark:border-green-800 dark:text-green-400">
                          <Unlock className="w-4 h-4" />
                          {t('signing:eimzo.connected')}
                        </div>

                        <Select
                          value={eimzo.selectedCertificate?.serialNumber || ''}
                          onValueChange={handleSelectCertificate}
                        >
                          <SelectTrigger className="w-full h-auto py-2">
                            <SelectValue placeholder={t('signing:eimzo.selectCertificate')}>
                              {eimzo.selectedCertificate && (
                                <span className="font-medium text-xs">
                                  {eimzo.selectedCertificate.fullName}
                                </span>
                              )}
                            </SelectValue>
                          </SelectTrigger>
                          <SelectContent className="max-h-80">
                            {eimzo.certificates.map((cert) => (
                              <SelectItem
                                key={cert.serialNumber || cert.id}
                                value={cert.serialNumber || cert.id}
                                disabled={cert.isExpired}
                                className={cert.isExpired ? 'opacity-50' : ''}
                              >
                                <div className="flex flex-col min-w-0 pr-6 py-1">
                                  <span className="font-medium text-foreground truncate">
                                    {cert.O && !cert.O.includes('.')
                                      ? cert.O
                                      : cert.fullName}
                                  </span>
                                  <span className="text-xs text-muted-foreground">
                                    {t('signing:candidates.pinfl')}: {maskNationalId(cert.PINFL)}
                                  </span>
                                </div>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>

                        <Button
                          className="w-full text-white font-medium"
                          style={{
                            background:
                              'linear-gradient(135deg, #9333EA 0%, #A855F7 100%)',
                          }}
                          onClick={handleGuestAuth}
                          disabled={
                            authLoading || !eimzo.selectedCertificate
                          }
                        >
                          {authLoading ? (
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          ) : (
                            <Shield className="w-4 h-4 mr-2" />
                          )}
                          {t('signing:guest.authAndSign')}
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ) : (
                <SigningPanel
                  fileId={file.id}
                  documentId={file.id}
                  approvalState={docType}
                  document={file}
                  isGuest={true}
                  guestToken={guestToken}
                  onSigningComplete={() => { }}
                />
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
